import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL } from './lib/api-config';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';
import './B2BCompanies.css';

function B2BCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDeals, setCompanyDeals] = useState([]);

  // Formulärdata
  const [formData, setFormData] = useState({
    name: '',
    organizationNumber: '',
    website: '',
    industry: '',
    size: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    country: 'Sverige',
    billingEmail: '',
    billingAddress: '',
    phone: '',
    notes: ''
  });

  // Hämta företag
  const fetchCompanies = useCallback(async () => {
    try {
      setError('');
      const response = await axios.get(`${API_URL}/b2b-companies`, {
        withCredentials: true
      });
      setCompanies(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Fel vid hämtning av företag:', error);
      setError('Kunde inte hämta företag. Kontrollera din anslutning.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Hämta deals för ett företag
  const fetchCompanyDeals = async (companyId) => {
    try {
      const response = await axios.get(`${API_URL}/deals?b2bCompanyId=${companyId}`, {
        withCredentials: true
      });
      setCompanyDeals(response.data.data || []);
    } catch (error) {
      console.error('Fel vid hämtning av deals:', error);
    }
  };

  // Hantera formulärändringar
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Öppna företagsdetaljer
  const handleViewCompany = async (company) => {
    setSelectedCompany(company);
    await fetchCompanyDeals(company.id);
  };

  // Redigera företag
  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || '',
      organizationNumber: company.organizationNumber || '',
      website: company.website || '',
      industry: company.industry || '',
      size: company.size || '',
      streetAddress: company.streetAddress || '',
      postalCode: company.postalCode || '',
      city: company.city || '',
      country: company.country || 'Sverige',
      billingEmail: company.billingEmail || '',
      billingAddress: company.billingAddress || '',
      phone: company.phone || '',
      notes: company.notes || ''
    });
    setShowForm(true);
  };

  // Spara företag
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Företagsnamn krävs');
      return;
    }

    try {
      const config = { withCredentials: true };

      if (editingCompany) {
        await axios.put(`${API_URL}/b2b-companies/${editingCompany.id}`, formData, config);
        setSuccess('Företag uppdaterat');
      } else {
        await axios.post(`${API_URL}/b2b-companies`, formData, config);
        setSuccess('Företag skapat');
      }

      setShowForm(false);
      setEditingCompany(null);
      resetForm();
      fetchCompanies();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte spara företag');
    }
  };

  // Ta bort företag
  const handleDelete = async (id) => {
    if (!window.confirm('Vill du verkligen ta bort detta företag?')) return;

    try {
      await axios.delete(`${API_URL}/b2b-companies/${id}`, {
        withCredentials: true
      });
      setSuccess('Företag borttaget');
      fetchCompanies();
      if (selectedCompany?.id === id) {
        setSelectedCompany(null);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte ta bort företag');
    }
  };

  // Återställ formulär
  const resetForm = () => {
    setFormData({
      name: '',
      organizationNumber: '',
      website: '',
      industry: '',
      size: '',
      streetAddress: '',
      postalCode: '',
      city: '',
      country: 'Sverige',
      billingEmail: '',
      billingAddress: '',
      phone: '',
      notes: ''
    });
  };

  // Filtrera företag
  const filteredCompanies = companies.filter(company => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      company.name?.toLowerCase().includes(query) ||
      company.organizationNumber?.toLowerCase().includes(query) ||
      company.industry?.toLowerCase().includes(query) ||
      company.city?.toLowerCase().includes(query)
    );
  });

  // Formatera pengar
  const formatCurrency = (value) => {
    if (!value) return '0 kr';
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <main className="main-content">
        <LoadingSpinner message="Laddar företag..." />
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="page-header">
        <h2>B2B-företag</h2>
        <div className="header-actions">
          <input
            type="search"
            placeholder="Sök företag..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setEditingCompany(null);
              setShowForm(true);
            }}
          >
            + Nytt företag
          </button>
        </div>
      </div>

      <AlertMessage error={error} success={success} />

      <div className="companies-layout">
        {/* Företagslista */}
        <div className="companies-list-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Företag</th>
                <th>Bransch</th>
                <th>Stad</th>
                <th>Storlek</th>
                <th>Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-message">
                    {searchQuery ? 'Inga företag matchar sökningen' : 'Inga företag ännu'}
                  </td>
                </tr>
              ) : (
                filteredCompanies.map(company => (
                  <tr
                    key={company.id}
                    className={selectedCompany?.id === company.id ? 'selected' : ''}
                    onClick={() => handleViewCompany(company)}
                  >
                    <td>
                      <div className="company-name">{company.name}</div>
                      {company.organizationNumber && (
                        <div className="company-orgno">{company.organizationNumber}</div>
                      )}
                    </td>
                    <td>{company.industry || '-'}</td>
                    <td>{company.city || '-'}</td>
                    <td>{company.size || '-'}</td>
                    <td>
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(company)}
                        >
                          Redigera
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(company.id)}
                        >
                          Ta bort
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Detaljpanel */}
        {selectedCompany && (
          <div className="company-detail-panel">
            <div className="panel-header">
              <h3>{selectedCompany.name}</h3>
              <button
                className="btn-close"
                onClick={() => setSelectedCompany(null)}
              >
                ×
              </button>
            </div>

            <div className="panel-content">
              <section className="detail-section">
                <h4>Företagsinformation</h4>
                <div className="detail-grid">
                  {selectedCompany.organizationNumber && (
                    <div className="detail-item">
                      <label>Org.nummer</label>
                      <span>{selectedCompany.organizationNumber}</span>
                    </div>
                  )}
                  {selectedCompany.website && (
                    <div className="detail-item">
                      <label>Webbplats</label>
                      <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer">
                        {selectedCompany.website}
                      </a>
                    </div>
                  )}
                  {selectedCompany.industry && (
                    <div className="detail-item">
                      <label>Bransch</label>
                      <span>{selectedCompany.industry}</span>
                    </div>
                  )}
                  {selectedCompany.size && (
                    <div className="detail-item">
                      <label>Storlek</label>
                      <span>{selectedCompany.size} anställda</span>
                    </div>
                  )}
                  {selectedCompany.phone && (
                    <div className="detail-item">
                      <label>Telefon</label>
                      <span>{selectedCompany.phone}</span>
                    </div>
                  )}
                </div>
              </section>

              {(selectedCompany.streetAddress || selectedCompany.city) && (
                <section className="detail-section">
                  <h4>Adress</h4>
                  <div className="address-block">
                    {selectedCompany.streetAddress && <div>{selectedCompany.streetAddress}</div>}
                    {(selectedCompany.postalCode || selectedCompany.city) && (
                      <div>{selectedCompany.postalCode} {selectedCompany.city}</div>
                    )}
                    {selectedCompany.country && <div>{selectedCompany.country}</div>}
                  </div>
                </section>
              )}

              {selectedCompany.notes && (
                <section className="detail-section">
                  <h4>Anteckningar</h4>
                  <p className="notes-text">{selectedCompany.notes}</p>
                </section>
              )}

              <section className="detail-section">
                <h4>Affärsmöjligheter ({companyDeals.length})</h4>
                {companyDeals.length === 0 ? (
                  <p className="empty-message">Inga affärsmöjligheter</p>
                ) : (
                  <div className="deals-list">
                    {companyDeals.map(deal => (
                      <div key={deal.id} className="deal-item">
                        <div className="deal-info">
                          <span className="deal-name">{deal.name}</span>
                          <span
                            className="deal-stage"
                            style={{ backgroundColor: deal.stage?.color || '#9CA3AF' }}
                          >
                            {deal.stage?.name}
                          </span>
                        </div>
                        <div className="deal-value">{formatCurrency(deal.expectedValue)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>
        )}
      </div>

      {/* Formulärmodal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCompany ? 'Redigera företag' : 'Nytt företag'}</h3>
              <button className="btn-close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group form-group-full">
                  <label>Företagsnamn *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Organisationsnummer</label>
                  <input
                    type="text"
                    name="organizationNumber"
                    value={formData.organizationNumber}
                    onChange={handleInputChange}
                    placeholder="XXXXXX-XXXX"
                  />
                </div>

                <div className="form-group">
                  <label>Webbplats</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://"
                  />
                </div>

                <div className="form-group">
                  <label>Bransch</label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    placeholder="t.ex. IT, Bygg, Finans"
                  />
                </div>

                <div className="form-group">
                  <label>Företagsstorlek</label>
                  <select
                    name="size"
                    value={formData.size}
                    onChange={handleInputChange}
                  >
                    <option value="">Välj...</option>
                    <option value="1-10">1-10 anställda</option>
                    <option value="11-50">11-50 anställda</option>
                    <option value="51-200">51-200 anställda</option>
                    <option value="201-500">201-500 anställda</option>
                    <option value="500+">500+ anställda</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-section-title">Adress</div>

                <div className="form-group form-group-full">
                  <label>Gatuadress</label>
                  <input
                    type="text"
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Postnummer</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Stad</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Land</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-section-title">Fakturering</div>

                <div className="form-group">
                  <label>Faktura-email</label>
                  <input
                    type="email"
                    name="billingEmail"
                    value={formData.billingEmail}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group form-group-full">
                  <label>Fakturaadress (om annan än ovan)</label>
                  <textarea
                    name="billingAddress"
                    value={formData.billingAddress}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>

                <div className="form-group form-group-full">
                  <label>Anteckningar</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  Avbryt
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCompany ? 'Spara ändringar' : 'Skapa företag'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

export default B2BCompanies;
